import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SaveOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import {
  Button,
  Col,
  Form,
  Input,
  Row,
  Select,
  Spin,
  Switch,
  message,
} from 'antd';
import { API } from '@/services/api.service';
import { ENTITY_TYPES, INVESTMENT_TYPES, errorText } from './format';

const settlementLabel = (s) => `${s.code} · ${s.name} (${s.municipality})`;

/**
 * Header of the form (B7–B15). Name, address and postal code fall back to the
 * company's own data when left empty; the settlement number and the activity
 * code are not on the company and have to be filled once.
 */
export default function HeaderTab({ data, companyId, onSaved }) {
  const { t } = useTranslation('diReports');
  const [form] = Form.useForm();
  const [options, setOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const p = data.profile;
  const g = data.company.generalData;

  useEffect(() => {
    form.setFieldsValue({
      name: p.header?.name || '',
      street: p.header?.street || '',
      place: p.header?.place || '',
      postalCode: p.header?.postalCode || '',
      settlementCode: p.header?.settlementCode || null,
      activityCode: p.header?.activityCode || '',
      entityType: p.entityType,
      investmentType: p.investmentType,
      obligor: p.obligor !== false,
      manual: Boolean(p.manual),
      manualReason: p.manualReason || '',
      note: p.note || '',
    });
    setOptions(
      data.settlement
        ? [
            {
              value: data.settlement.code,
              label: settlementLabel(data.settlement),
            },
          ]
        : []
    );
  }, [data, form, p]);

  const search = async (q) => {
    if (!q || q.length < 2) return;
    setSearching(true);
    try {
      const found = await API.diReports.searchSettlements(q);
      setOptions(
        found.map((s) => ({ value: s.code, label: settlementLabel(s) }))
      );
    } finally {
      setSearching(false);
    }
  };

  const save = useMutation({
    mutationFn: (v) =>
      API.diReports.saveProfile(companyId, {
        header: {
          name: v.name,
          street: v.street,
          place: v.place,
          postalCode: v.postalCode,
          settlementCode: v.settlementCode,
          activityCode: v.activityCode,
        },
        entityType: v.entityType,
        investmentType: v.investmentType,
        obligor: v.obligor,
        manual: v.manual,
        manualReason: v.manualReason,
        note: v.note,
      }),
    onSuccess: () => {
      message.success(t('messages.saved'));
      onSaved();
    },
    onError: async (e) => message.error(await errorText(e)),
  });

  return (
    <Form form={form} layout="vertical" onFinish={(v) => save.mutate(v)}>
      <Row gutter={16}>
        <Col span={24}>
          <Form.Item
            name="name"
            label={t('header.name')}
            extra={t('header.fallback', { value: data.company.name })}
          >
            <Input placeholder={data.company.name} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="street" label={t('header.street')}>
            <Input placeholder={g.address} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="place" label={t('header.place')}>
            <Input placeholder={g.city} />
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item name="postalCode" label={t('header.postalCode')}>
            <Input
              placeholder={g.zip || data.settlement?.postalCode}
              maxLength={5}
            />
          </Form.Item>
        </Col>
        <Col span={16}>
          <Form.Item
            name="settlementCode"
            label={t('header.settlement')}
            extra={t('header.settlementHelp')}
          >
            <Select
              showSearch
              allowClear
              filterOption={false}
              onSearch={search}
              notFoundContent={searching ? <Spin size="small" /> : null}
              options={options}
              placeholder={t('header.settlementPlaceholder')}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="activityCode"
            label={t('header.activity')}
            extra={t('header.activityHelp')}
            rules={[
              { pattern: /^\d{4,6}$/, message: t('header.activityInvalid') },
            ]}
          >
            <Input maxLength={6} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="entityType" label={t('header.entityType')}>
            <Select
              options={ENTITY_TYPES.map((v) => ({
                value: v,
                label: `${v} — ${t(`entityType.${v}`)}`,
              }))}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="investmentType" label={t('header.investmentType')}>
            <Select
              options={INVESTMENT_TYPES.map((v) => ({
                value: v,
                label: `${v} — ${t(`investmentType.${v}`)}`,
              }))}
            />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item name="note" label={t('header.note')}>
            <Input.TextArea autoSize={{ minRows: 1, maxRows: 4 }} />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item
            name="obligor"
            label={t('header.obligor')}
            valuePropName="checked"
            extra={t('header.obligorHelp')}
          >
            <Switch />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="manual"
            label={t('header.manual')}
            valuePropName="checked"
            extra={t('header.manualHelp')}
          >
            <Switch />
          </Form.Item>
        </Col>
        <Col span={16}>
          <Form.Item
            noStyle
            shouldUpdate={(a, b) => a.manual !== b.manual}
          >
            {({ getFieldValue }) =>
              getFieldValue('manual') && (
                <Form.Item name="manualReason" label={t('header.manualReason')}>
                  <Input
                    maxLength={200}
                    placeholder={t('header.manualDefault')}
                  />
                </Form.Item>
              )
            }
          </Form.Item>
        </Col>
      </Row>
      <div className="flex justify-end">
        <Button
          shape="round"
          type="primary"
          htmlType="submit"
          icon={<SaveOutlined />}
          loading={save.isPending}
        >
          {t('actions.save')}
        </Button>
      </div>
    </Form>
  );
}
